import React, { useState, useRef } from 'react';
import { AnimalCardProps } from '../types/Animal';
import './AnimalCard.css';

interface AnimalCardPropsWithFavorites extends AnimalCardProps {
  isFavorite?: boolean;
  onToggleFavorite?: (animalId: string) => void;
}

const AnimalCard: React.FC<AnimalCardPropsWithFavorites> = ({ 
  animal, 
  onClick, 
  isFavorite = false, 
  onToggleFavorite 
}) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const currentPhotoUrl = animal.photos[currentPhotoIndex] || animal.photos[0] || '/placeholder-animal.svg';
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const isTouchDevice = 'ontouchstart' in window;

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.log('Image failed to load:', e.currentTarget.src);
    
    // Try alternative URL formats if the first one fails
    const currentSrc = e.currentTarget.src;
    
    if (currentSrc.includes('export=download')) {
      // If download format failed, try the view format
      const fileId = currentSrc.match(/id=([^&]+)/)?.[1];
      if (fileId) {
        console.log('Trying uc?export=view format...');
        e.currentTarget.src = `https://drive.google.com/uc?export=view&id=${fileId}`;
        return;
      }
    } else if (currentSrc.includes('export=view')) {
      // If view format failed, try thumbnail format
      const fileId = currentSrc.match(/id=([^&]+)/)?.[1];
      if (fileId) {
        console.log('Trying thumbnail format...');
        e.currentTarget.src = `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000-h1000`;
        return;
      }
    }
    
    // If all formats fail, use placeholder
    e.currentTarget.src = '/placeholder-animal.svg';
  };


  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleFavorite) {
      onToggleFavorite(animal.id);
    }
  };

  const handlePhotoNavigation = (e: React.MouseEvent, direction: 'prev' | 'next') => {
    e.stopPropagation();
    if (direction === 'next') {
      const newIndex = currentPhotoIndex === animal.photos.length - 1 ? 0 : currentPhotoIndex + 1;
      setCurrentPhotoIndex(newIndex);
    } else {
      const newIndex = currentPhotoIndex === 0 ? animal.photos.length - 1 : currentPhotoIndex - 1;
      setCurrentPhotoIndex(newIndex);
    }
  };

  const handleDotClick = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setCurrentPhotoIndex(index);
  };

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (animal.photos.length <= 1) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const centerX = rect.width / 2;
    
    if (clickX > centerX) {
      // Right half - next photo
      const newIndex = currentPhotoIndex === animal.photos.length - 1 ? 0 : currentPhotoIndex + 1;
      setCurrentPhotoIndex(newIndex);
    } else {
      // Left half - previous photo
      const newIndex = currentPhotoIndex === 0 ? animal.photos.length - 1 : currentPhotoIndex - 1;
      setCurrentPhotoIndex(newIndex);
    }
  };

  return (
    <div className="animal-card" onClick={() => {
      console.log(`🎯 Card clicked: ${animal.name} on photo index ${currentPhotoIndex}`);
      onClick(animal, currentPhotoIndex);
    }}>
      <div className="animal-card__image-container">
        <img
          src={currentPhotoUrl}
          alt={animal.name}
          className="animal-card__image"
          onError={handleImageError}
          onClick={handleImageClick}
          loading="lazy"
        />
        {animal.photos.length > 1 && (
          <>
            <button 
              className="photo-nav photo-nav--left"
              onClick={(e) => handlePhotoNavigation(e, 'prev')}
              title="Previous photo"
            >
              ‹
            </button>
            <button 
              className="photo-nav photo-nav--right"
              onClick={(e) => handlePhotoNavigation(e, 'next')}
              title="Next photo"
            >
              ›
            </button>
          </>
        )}
        {animal.photos.length > 1 && (
          <div className="photo-dots">
            {animal.photos.map((_, index) => (
              <button
                key={index}
                className={`photo-dot ${index === currentPhotoIndex ? 'active' : ''}`}
                onClick={(e) => handleDotClick(e, index)}
                title={`Photo ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
      <div className="animal-card__content">
        <h3 className="animal-card__name">{animal.name}</h3>
        <p className="animal-card__breed">{animal.breed || '\u00A0'}</p>
        <p className="animal-card__sex">{animal.sex}</p>
        <div className="animal-card__details">
          <span className="animal-card__age">{animal.age}</span>
          <button 
            className={`animal-card__favorite ${isFavorite ? 'favorited' : ''}`}
            onClick={handleFavoriteClick}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnimalCard;